import supabaseConfig from '@/config/supabase/supabaseConfig.json';
import * as Sentry from '@sentry/react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Define types for the returned services for better type safety
export interface SupabaseServices {
  client: SupabaseClient;
  auth: {
    signOut: () => Promise<void>;
    getSession: () => Promise<any>;
  };
}

let supabaseServices: SupabaseServices | null = null;

const initializeSupabaseApp = (token?: string): SupabaseServices => {
  // Always create a new client when a token is provided to ensure fresh authentication
  // Only reuse the existing client if no token is provided
  if (supabaseServices && !token) {
    return supabaseServices;
  }

  try {
    // Use environment variables if available, otherwise fall back to config file
    const supabaseUrl = supabaseConfig.supabaseUrl;
    const supabaseAnonKey = supabaseConfig.supabaseKey;

    // Initialize Supabase client with configuration
    // If a token is provided, it will be used in the Authorization header for all requests
    const clientOptions: any = {};

    // Add custom Authorization header if token is provided
    if (token) {
      const AUTHORIZATION_TOKEN = `Bearer ${token}`;

      clientOptions.global = {
        headers: {
          Authorization: AUTHORIZATION_TOKEN,
        },
      };

      clientOptions.realtime = {
        headers: {
          apikey: AUTHORIZATION_TOKEN, // custom token for realtime authentication
        },
        params: {
          apikey: supabaseAnonKey, // the supabase anon key
        },
        worker: true, // enable worker for realtime connection
      };
    }

    const client: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, clientOptions);

    // Set auth for realtime connection if token is provided
    if (token) {
      client.realtime.setAuth(token);
    }

    console.log('✅ Supabase client initialized successfully with custom token.');

    // Auth services
    const authServices = {
      // Sign out
      signOut: async (): Promise<void> => {
        try {
          const { error } = await client.auth.signOut();
          if (error) throw error;
          console.log('✅ User signed out successfully');
        } catch (error) {
          console.error('❌ Error signing out:', error);
          Sentry.captureException(error);
          throw error;
        }
      },

      // Get current session
      getSession: async () => {
        try {
          const {
            data: { session },
            error,
          } = await client.auth.getSession();
          if (error) throw error;
          return session;
        } catch (error) {
          console.error('❌ Error getting session:', error);
          Sentry.captureException(error);
          return null;
        }
      },
    };

    supabaseServices = {
      client,
      auth: authServices,
    };

    return supabaseServices;
  } catch (error) {
    console.error('🔥 Failed to initialize Supabase client:', error);
    Sentry.captureException(error);
    throw error;
  }
};

export { initializeSupabaseApp, supabaseServices };
